import { useCallback, useEffect, useRef, useState } from "react";

import { type ExchangeAccount, exchangeAccountsEndpoints } from "../../../api";
import { useStrapiList } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import { hasDataChanged } from "../../../utils/hasDataChanged";
import { getFromCache, setToCache } from "../../../utils/memoryCache";
import handleError from "../../utils/handleError";

interface UseExchangeAccountsProps {
  cacheKey?: string;
  pagination?: { pageSize?: number };
  mine?: boolean;
  autoLoad?: boolean;
  [key: string]: unknown;
}

// autoLoad, sort, pagination, filters, populate
const useExchangeAccounts = (props?: UseExchangeAccountsProps) => {
  const { cacheKey, pagination, mine } = props || {};
  // Include pageSize in cache key to invalidate when pagination changes
  const fullCacheKey = cacheKey ? `${cacheKey}_${pagination?.pageSize}` : null;
  const cached = fullCacheKey ? getFromCache<ExchangeAccount[]>(fullCacheKey) : null;
  const [cachedItems, setCachedItems] = useState<ExchangeAccount[]>(cached?.data || []);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const hasInitialCache = useRef(!!cached);

  // Re-seed when the cache key changes. The initial state is evaluated once
  // per mount, so navigating between two entities served by the same route
  // component changed the key while keeping the component instance, and the
  // hook kept returning the previous entity's data as if it were current.
  const seededCacheKeyRef = useRef(cacheKey);
  if (seededCacheKeyRef.current !== cacheKey) {
    seededCacheKeyRef.current = cacheKey;
    const nextCached = cacheKey ? getFromCache<ExchangeAccount[]>(cacheKey) : null;
    setCachedItems(nextCached?.data || []);
    hasInitialCache.current = !!nextCached;
  }
  const mountedRef = useRef(true);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Stable load function - only changes when mine changes
  const isMine = !!mine;
  const load = useCallback((params: Record<string, unknown>) => loadExchangeAccounts(params, isMine), [isMine]);

  // Stable error handler
  const onLoadError = useCallback(
    (e: unknown) => handleError(getErrorMessage(e, "Exchange accounts loading failed")),
    []
  );

  const {
    items: exchangeAccounts,
    refresh,
    isLoading,
    ...other
  } = useStrapiList<ExchangeAccount>({
    ...props,
    load,
    onLoadError,
  });

  // Update cache when fresh data arrives (only if changed)
  useEffect(() => {
    if (!mountedRef.current) return;
    if (!isLoading && exchangeAccounts?.length > 0 && fullCacheKey) {
      // Only update if data actually changed
      if (hasDataChanged(cachedItems, exchangeAccounts)) {
        setToCache(fullCacheKey, exchangeAccounts);
        setCachedItems(exchangeAccounts);
      }
      setIsRefreshing(false);
    }
  }, [exchangeAccounts, isLoading, fullCacheKey, cachedItems]);

  // Track refreshing state
  useEffect(() => {
    if (!mountedRef.current) return;
    if (isLoading && hasInitialCache.current) {
      setIsRefreshing(true);
    }
  }, [isLoading]);

  // Use cached data if available while loading
  const items = fullCacheKey && cachedItems.length > 0 ? cachedItems : exchangeAccounts;
  const effectiveLoading = fullCacheKey ? !hasInitialCache.current && isLoading : isLoading;

  return {
    exchangeAccounts: items,
    refresh,
    isLoading: effectiveLoading,
    isRefreshing,
    ...other,
  };
};

export const loadExchangeAccounts = async (params: Record<string, unknown>, mine: boolean) => {
  try {
    return mine
      ? exchangeAccountsEndpoints.getMyExchangeAccounts(params)
      : exchangeAccountsEndpoints.getExchangeAccounts(params);
  } catch (e: unknown) {
    handleError(getErrorMessage(e, "Exchange accounts loading failed"));
    return undefined;
  }
};

export default useExchangeAccounts;
